import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  title?: string;
  id?: string;
}

const tones = {
  dark: {
    group: 'gap-1 bg-black/40 backdrop-blur-md p-1 rounded-lg border border-white/10',
    item: 'px-3 py-1.5 text-[10px] font-bold rounded-md transition-colors gap-1',
    active: 'bg-blue-600 text-white shadow-xs',
    idle: 'text-slate-400 hover:text-white',
  },
  light: {
    group: 'gap-1.5 bg-slate-200/70 p-1 rounded-xl',
    item: 'px-3 py-1 text-xs font-semibold rounded-lg transition-all gap-1.5',
    active: 'bg-white text-slate-900 font-bold shadow-xs',
    idle: 'text-slate-600 hover:text-slate-900',
  },
} as const;

export interface SegmentedControlProps<T extends string> {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  tone?: keyof typeof tones;
  label: string;
  className?: string;
  itemClassName?: string;
}

/** A row of mutually exclusive toggle buttons (view modes, tabs, before/after). */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  tone = 'dark',
  label,
  className,
  itemClassName,
}: SegmentedControlProps<T>) {
  const styles = tones[tone];
  return (
    <div role="group" aria-label={label} className={cn('flex items-center', styles.group, className)}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            id={option.id}
            type="button"
            title={option.title}
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn('flex items-center', styles.item, active ? styles.active : styles.idle, itemClassName)}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
